// FRS 4.2.2 — Wizard สร้างเอกสาร ออพ.๐๑ + ออพ.๐๒ (Bundle)
// 4 ขั้น: (1) เลือกประเภท+เหตุผล (2) MANUAL (3) AUTO read-only (4) Preview+Submit
// รองรับ Revoke Pattern + Audit Log, Text Overflow Layer 1 (max_chars + counter)
//
// NOTE: ปิด a11y lint บางตัวสำหรับหน้านี้ (internal tool) — ความถูกต้อง a11y
// (label association / nav semantics / emoji) เป็นงาน hardening รอบถัดไป
/* eslint-disable jsx-a11y/label-has-for, jsx-a11y/label-has-associated-control, jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, jsx-a11y/accessible-emoji */
import React from 'react';
import Layout from '../../components/layout/Layout';
import i18n from '../../i18n';
import {
  listForms,
  createForm,
  getForm,
  deleteForm,
  getAutoData,
  getManualData,
  saveManualData,
  previewPdf,
  submitForm,
  revokeForm,
  getAuditLog,
  getRenderAudit,
} from '../../redux/forms/forms_actions';

// max_chars ต่อฟิลด์ (Layer 1) — ตรงกับ backend form_field_config.py
const LIMIT = {
  full_name: 80,
  id_card: 13,
  license_type: 60,
  license_level: 30,
  license_no: 60,
  cert_no: 60,
  phone: 10,
  construction_cost: 15,
  designer: 80,
  addr_house_no: 20,
  addr_moo: 3,
  addr_soi: 60,
  addr_road: 60,
  addr_sub_district: 50,
  addr_district: 50,
  addr_province: 30,
  addr_postal_code: 5,
};

const ADDRESS_FIELDS = [
  ['addr_house_no', 'เลขที่'],
  ['addr_moo', 'หมู่ที่'],
  ['addr_soi', 'ตรอก/ซอย'],
  ['addr_road', 'ถนน'],
  ['addr_sub_district', 'ตำบล/แขวง'],
  ['addr_district', 'อำเภอ/เขต'],
  ['addr_province', 'จังหวัด'],
  ['addr_postal_code', 'รหัสไปรษณีย์'],
];

const emptyAddr = () => ({
  addr_house_no: '',
  addr_moo: '',
  addr_soi: '',
  addr_road: '',
  addr_sub_district: '',
  addr_district: '',
  addr_province: '',
  addr_postal_code: '',
});

// input + char counter (Layer 1)
const CountedInput = ({
  field,
  value,
  onChange,
  label,
  type = 'text',
  placeholder,
  hint,
}) => {
  const max = LIMIT[field];
  const len = (value || '').length;
  const over = max && len > max;
  return (
    <div style={{ marginBottom: 8 }}>
      {label && (
        <label style={{ display: 'block', fontSize: 13 }}>{label}</label>
      )}
      <input
        className="form-control"
        type={type}
        value={value || ''}
        maxLength={max}
        placeholder={placeholder}
        onChange={e => onChange(field, e.target.value)}
      />
      {hint && (
        <small style={{ display: 'block', color: '#888' }}>{hint}</small>
      )}
      {max && (
        <small style={{ color: over ? 'red' : '#888' }}>
          {len}/{max}
        </small>
      )}
    </div>
  );
};

// ใบประกอบวิชาชีพ — ตามกฎหมายมีค่าตายตัว (PM) → dropdown
const LICENSE_TYPES = ['วิศวกรรมควบคุม', 'สถาปัตยกรรมควบคุม'];
const LICENSE_LEVELS = ['ภาคีพิเศษ', 'ภาคี', 'สามัญ', 'วุฒิ']; // เรียงเล็ก→ใหญ่

const SelectInput = ({ field, value, onChange, label, options }) => (
  <div style={{ marginBottom: 8 }}>
    {label && <label style={{ display: 'block', fontSize: 13 }}>{label}</label>}
    <select
      className="form-control"
      value={value || ''}
      onChange={e => onChange(field, e.target.value)}
    >
      <option value="">— เลือก —</option>
      {options.map(o => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  </div>
);

class FormWizardPage extends React.Component {
  state = {
    projectId: null,
    forms: [],
    formId: null,
    form: null,
    step: 1,
    purpose: 'a1',
    inspector: {
      ...emptyAddr(),
      license_type: '',
      license_level: '',
      license_no: '',
      license_expire: '',
      cert_no: '',
      cert_expire: '',
      phone: '',
    },
    owner: { ...emptyAddr(), full_name: '', age: '', id_card: '', phone: '' },
    designer: '',
    construction_cost: '',
    // ออพ.๐๒ รูปแบบอาคาร/อุปกรณ์ — manual (default จากระบบ แก้ได้ขั้น 3) WWR ไม่อยู่ (AUTO)
    manual: {
      floor_height: '',
      wall: '',
      glass: '',
      roof: '',
      ac: '',
      lighting: '',
      hot_water: '',
    },
    auto: null,
    loadingAuto: false,
    autoError: null,
    previewUrl: null,
    auditLog: [],
    renderAudit: [],
    revokeReason: '',
    showRevoke: false,
    error: null,
    loading: false,
  };

  componentDidMount() {
    const projectId = this.props.match.params.projectId;
    this.setState({ projectId });
    this.refreshList(projectId);
  }

  refreshList = projectId => {
    listForms(projectId)
      .then(data => {
        // รองรับทุก shape: DatatablesPagination ({data}), DRF ({results}), array ตรง ๆ
        const list = Array.isArray(data)
          ? data
          : data.data || data.results || [];
        this.setState({ forms: list });
      })
      .catch(() => this.setState({ forms: [] }));
  };

  // ---- ขั้นที่ 1 ----
  handleCreate = () => {
    this.setState({ loading: true, error: null });
    createForm(this.state.projectId, this.state.purpose)
      .then(form => this.openForm(form.id))
      .catch(err => this.setState({ error: this.msg(err), loading: false }));
  };

  openForm = formId => {
    this.setState({ loading: true, formId });
    Promise.all([getForm(formId), getManualData(formId)])
      .then(([form, manual]) => {
        const p = manual.inspector_profile || {};
        const o = manual.building_owner || {};
        // เอกสารที่ออกแล้ว (locked) → เปิดที่ขั้น 4 (ตรวจสอบ+ประวัติ+ดาวน์โหลด) อ่านอย่างเดียว
        // เอกสาร draft → เปิดที่ขั้น 2 ตามเดิมเพื่อกรอก/แก้ไขต่อ
        const isLocked = form.status === 'locked';
        this.setState({
          form,
          step: isLocked ? 4 : 2,
          loading: false,
          purpose: form.purpose,
          designer: form.designer || '',
          construction_cost: form.construction_cost || '',
          inspector: { ...this.state.inspector, ...p },
          owner: { ...this.state.owner, ...o },
        });
        if (isLocked) {
          this.loadPreview();
          this.loadAudit();
        }
      })
      .catch(err => this.setState({ error: this.msg(err), loading: false }));
  };

  // ลบเอกสาร draft (มี confirm) — locked ลบไม่ได้ (backend ปฏิเสธ)
  handleDelete = formId => {
    if (!window.confirm('ยืนยันลบเอกสารฉบับร่างนี้?')) return;
    this.setState({ loading: true, error: null });
    deleteForm(formId)
      .then(() => {
        this.setState({ loading: false });
        this.refreshList(this.state.projectId);
      })
      .catch(err => this.setState({ error: this.msg(err), loading: false }));
  };

  // ---- ขั้นที่ 2: บันทึก MANUAL ----
  saveManual = nextStep => {
    const {
      formId,
      inspector,
      owner,
      designer,
      construction_cost,
      purpose,
    } = this.state;
    const payload = {
      purpose,
      designer,
      construction_cost: construction_cost || null,
      ...inspector,
      building_owner: owner,
    };
    this.setState({ loading: true, error: null });
    saveManualData(formId, payload)
      .then(() => {
        this.setState({ loading: false });
        this.goToStep(nextStep);
      })
      .catch(err => this.setState({ error: this.msg(err), loading: false }));
  };

  // ไปยัง step ที่ระบุ — โหลดข้อมูลของ step นั้นให้อัตโนมัติ (กันค้าง/ว่างเมื่อกดแท็บตรง ๆ)
  goToStep = step => {
    this.setState({ step });
    if (step === 3 && !this.state.auto) {
      this.loadAuto();
    }
    if (step === 4 && !this.state.previewUrl) {
      this.loadPreview();
    }
  };

  // ---- ขั้นที่ 3: โหลด AUTO จาก BEC engine ----
  loadAuto = () => {
    this.setState({ loadingAuto: true, autoError: null });
    getAutoData(this.state.formId)
      .then(auto => {
        // pre-fill manual: ค่าที่เคยบันทึกใน form ก่อน, ถ้าว่างใช้ default จากระบบ (AUTO)
        const f = this.state.form || {};
        const s = auto.structure || {};
        const p = auto.power || {};
        const manual = {
          floor_height: f.m_floor_height || s.floor_height || '',
          wall: f.m_wall_type || s.wall || '',
          glass: f.m_glass_type || s.glass || '',
          roof: f.m_roof_type || s.roof || '',
          ac: f.m_ac_type || p.ac || '',
          lighting: f.m_lighting_type || p.lighting || '',
          hot_water: f.m_hot_water_type || p.hot_water || '',
        };
        this.setState({ auto, manual, loadingAuto: false });
      })
      .catch(err =>
        this.setState({ autoError: this.msg(err), loadingAuto: false }),
      );
  };

  setManual = (field, v) =>
    this.setState({ manual: { ...this.state.manual, [field]: v } });

  // บันทึก manual (รูปแบบอาคาร/อุปกรณ์) แล้วไป Preview
  saveStructureAndPreview = () => {
    const m = this.state.manual;
    const payload = {
      m_floor_height: m.floor_height,
      m_wall_type: m.wall,
      m_glass_type: m.glass,
      m_roof_type: m.roof,
      m_ac_type: m.ac,
      m_lighting_type: m.lighting,
      m_hot_water_type: m.hot_water,
    };
    this.setState({ loading: true, error: null });
    saveManualData(this.state.formId, payload)
      .then(() => this.loadPreview())
      .catch(err => this.setState({ error: this.msg(err), loading: false }));
  };

  // อ่านข้อความ error — รองรับกรณี response เป็น Blob (จาก preview ที่ขอแบบ blob)
  extractError = (err, cb) => {
    const data = err && err.response && err.response.data;
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
      data
        .text()
        .then(t => {
          try {
            cb(JSON.parse(t).detail || t);
          } catch (e) {
            cb(t);
          }
        })
        .catch(() => cb('เกิดข้อผิดพลาด'));
    } else {
      cb(this.msg(err));
    }
  };

  // ---- ขั้นที่ 4: Preview ----
  loadPreview = () => {
    const { formId } = this.state;
    this.setState({ loading: true, error: null, step: 4 });
    Promise.all([previewPdf(formId), getRenderAudit(formId)])
      .then(([url, renderAudit]) =>
        this.setState({ previewUrl: url, renderAudit, loading: false }),
      )
      .catch(err =>
        this.extractError(err, m =>
          this.setState({ error: m, loading: false }),
        ),
      );
  };

  handleSubmit = () => {
    this.setState({ loading: true, error: null });
    submitForm(this.state.formId)
      .then(form => {
        this.setState({ form, loading: false });
        this.refreshList(this.state.projectId);
        this.loadAudit();
      })
      .catch(err => this.setState({ error: this.msg(err), loading: false }));
  };

  handleRevoke = () => {
    if (!this.state.revokeReason) {
      this.setState({ error: 'ต้องระบุเหตุผลในการถอนการส่งตรวจ' });
      return;
    }
    this.setState({ loading: true, error: null });
    revokeForm(this.state.formId, this.state.revokeReason)
      .then(form => {
        this.setState({
          form,
          showRevoke: false,
          revokeReason: '',
          loading: false,
        });
        this.loadAudit();
      })
      .catch(err => this.setState({ error: this.msg(err), loading: false }));
  };

  loadAudit = () => {
    getAuditLog(this.state.formId)
      .then(auditLog => this.setState({ auditLog }))
      .catch(() => {});
  };

  msg = err => (err && (err.detail || JSON.stringify(err))) || 'เกิดข้อผิดพลาด';

  // แปลง ISO timestamp → วันที่-เวลาแบบไทย (พ.ศ.) อ่านง่าย
  fmtDateTime = iso => {
    if (!iso) return '-';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const pad = n => (n < 10 ? '0' + n : '' + n);
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear() +
      543} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // แปลง action → คำไทย
  actionText = a => ({ submit: 'ส่งตรวจ', revoke: 'ถอนคำขอ' }[a] || a);

  // ภาษาปัจจุบัน (i18n) — ใช้เลือกข้อความ ไทย/อังกฤษ ของกล่องผลลัพธ์
  isThai = () => !(i18n.language || 'th').toLowerCase().startsWith('en');

  setInspector = (f, v) =>
    this.setState({ inspector: { ...this.state.inspector, [f]: v } });
  setOwner = (f, v) =>
    this.setState({ owner: { ...this.state.owner, [f]: v } });

  render() {
    const { step, form, error, loading } = this.state;
    const locked = form && form.status === 'locked';
    const breadcrumbItems = [
      { name: 'โครงการ', path: '/projects' },
      { name: 'ออพ.๐๑ / ออพ.๐๒', path: '/id' },
    ];
    return (
      <Layout history={this.props.history} breadcrumbItems={breadcrumbItems}>
        <div style={{ padding: 24, maxWidth: 1000, margin: '0 auto' }}>
          <h2 style={{ fontSize: 22 }}>
            สร้างเอกสารประกอบการยื่นขออนุญาต (แบบฟอร์ม ออพ. 01 และ ออพ.02)
          </h2>
          {error && <div className="alert alert-danger">{error}</div>}
          {/* ขั้น 4 มีสถานะของตัวเองอยู่ล่าง (ใกล้กล่องผลลัพธ์) — ไม่โชว์ซ้ำด้านบน */}
          {loading && step !== 4 && (
            <div className="alert alert-info">กำลังดำเนินการ...</div>
          )}

          {!this.state.formId && this.renderListAndCreate()}
          {this.state.formId && (
            <div>
              {this.renderStepNav()}
              {step === 1 && this.renderStep1()}
              {step === 2 && this.renderStep2()}
              {step === 3 && this.renderStep3()}
              {step === 4 && this.renderStep4(locked)}
            </div>
          )}
        </div>
      </Layout>
    );
  }

  renderListAndCreate() {
    return (
      <div>
        <div className="panel panel-default" style={{ padding: 16 }}>
          <h4>กรุณาระบุจุดประสงค์ของการจัดทำเอกสาร</h4>
          <label>
            <input
              type="radio"
              name="purpose"
              checked={this.state.purpose === 'a1'}
              onChange={() => this.setState({ purpose: 'a1' })}
            />{' '}
            ขอรับใบอนุญาตก่อสร้าง/ดัดแปลง
          </label>
          <br />
          <label>
            <input
              type="radio"
              name="purpose"
              checked={this.state.purpose === 'a5'}
              onChange={() => this.setState({ purpose: 'a5' })}
            />{' '}
            ขอรับใบรับรองการก่อสร้าง/ดัดแปลง
          </label>
          <br />
          <button
            className="btn btn-primary"
            onClick={this.handleCreate}
            style={{ marginTop: 8 }}
          >
            + สร้างเอกสาร
          </button>
        </div>
        <h4>ประวัติการจัดทำเอกสาร</h4>
        <table className="table table-bordered">
          <thead>
            <tr>
              <th>เลขที่เอกสาร</th>
              <th>ครั้งที่แก้ไข</th>
              <th>สถานะ</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {this.state.forms.map(f => (
              <tr key={f.id}>
                <td style={{ verticalAlign: 'middle' }}>
                  {f.doc_number || '(ยังไม่ส่งตรวจ)'}
                </td>
                <td style={{ verticalAlign: 'middle' }}>{f.revision_no}</td>
                <td style={{ verticalAlign: 'middle' }}>
                  {f.status === 'locked' ? 'เสร็จสิ้น' : 'ร่าง'}
                </td>
                <td style={{ verticalAlign: 'middle' }}>
                  <button
                    className="btn btn-link"
                    onClick={() => this.openForm(f.id)}
                  >
                    เปิด
                  </button>
                  {f.status !== 'locked' && (
                    <button
                      className="btn btn-link"
                      style={{ color: '#c9302c' }}
                      onClick={() => this.handleDelete(f.id)}
                    >
                      ลบ
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  renderStepNav() {
    const steps = [
      '1. ประเภทเอกสาร',
      '2. ข้อมูลทั่วไป',
      '3. ข้อมูลการตรวจประเมิน',
      '4. ตรวจสอบข้อมูล',
    ];
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <ul className="nav nav-pills" style={{ margin: 0 }}>
          {steps.map((label, i) => (
            <li key={i} className={this.state.step === i + 1 ? 'active' : ''}>
              <a
                onClick={() => this.goToStep(i + 1)}
                style={{ cursor: 'pointer' }}
              >
                {label}
              </a>
            </li>
          ))}
        </ul>
        {/* ย้อนกลับ — แยกออกจาก step pills ให้ชัดเจนว่าไม่ใช่ขั้นตอนที่ 5 */}
        <button
          type="button"
          className="btn btn-default btn-sm"
          style={{ marginLeft: 'auto' }}
          onClick={() => {
            this.setState({ formId: null, form: null, step: 1 });
            // refresh รายการเสมอ เพื่อให้เอกสารที่เพิ่งสร้าง/แก้ไข โผล่ทันที
            this.refreshList(this.state.projectId);
          }}
        >
          ← ย้อนกลับไปหน้ารายการ
        </button>
      </div>
    );
  }

  // หัวข้อ section แบบเด่น (มีกรอบ/พื้นหลัง) ตาม feedback PM
  sectionHeader(text) {
    return (
      <div
        style={{
          background: '#eef3f8',
          border: '1px solid #d3e0ea',
          borderRadius: 4,
          padding: '8px 12px',
          fontWeight: 'bold',
          fontSize: 15,
          margin: '16px 0 12px',
        }}
      >
        {text}
      </div>
    );
  }

  renderStep1() {
    return (
      <div>
        <h4>ประเภทเอกสาร: Bundle (ออพ.๐๑ + ออพ.๐๒) — สร้างคู่กันเสมอ</h4>
        <label>
          <input
            type="radio"
            checked={this.state.purpose === 'a1'}
            onChange={() => this.setState({ purpose: 'a1' })}
          />{' '}
          ขอรับใบอนุญาตก่อสร้าง/ดัดแปลง (อ.๑)
        </label>
        <br />
        <label>
          <input
            type="radio"
            checked={this.state.purpose === 'a5'}
            onChange={() => this.setState({ purpose: 'a5' })}
          />{' '}
          ขอรับใบรับรองการก่อสร้าง/ดัดแปลง (อ.๕)
        </label>
        <br />
        <button className="btn btn-primary" onClick={() => this.saveManual(2)}>
          ถัดไป →
        </button>
      </div>
    );
  }

  renderAddress(obj, setter, title) {
    return (
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 6 }}>
          {title}
        </div>
        <div
          style={{
            border: '1px solid #ddd',
            borderRadius: 4,
            padding: 12,
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 12,
          }}
        >
          {ADDRESS_FIELDS.map(([f, label]) => (
            <CountedInput
              key={f}
              field={f}
              label={label}
              value={obj[f]}
              onChange={setter}
            />
          ))}
        </div>
      </div>
    );
  }

  renderStep2() {
    const { inspector, owner } = this.state;
    return (
      <div>
        {this.sectionHeader('ข้อมูลผู้ตรวจประเมิน')}
        <SelectInput
          field="license_type"
          label="ประเภทของใบอนุญาตประกอบวิชาชีพ"
          value={inspector.license_type}
          options={LICENSE_TYPES}
          onChange={this.setInspector}
        />
        <SelectInput
          field="license_level"
          label="ระดับของใบอนุญาตประกอบวิชาชีพ"
          value={inspector.license_level}
          options={LICENSE_LEVELS}
          onChange={this.setInspector}
        />
        <CountedInput
          field="license_no"
          label="เลขทะเบียนใบประกอบวิชาชีพ"
          value={inspector.license_no}
          onChange={this.setInspector}
        />
        <div style={{ marginBottom: 8 }}>
          <label style={{ display: 'block', fontSize: 13 }}>
            วันหมดอายุใบประกอบวิชาชีพ
          </label>
          <input
            className="form-control"
            type="date"
            value={inspector.license_expire || ''}
            onChange={e => this.setInspector('license_expire', e.target.value)}
          />
        </div>
        <CountedInput
          field="cert_no"
          label="หนังสือรับรองผู้ตรวจประเมินเลขที่"
          value={inspector.cert_no}
          onChange={this.setInspector}
          placeholder="ผต.XXXX"
          hint="โปรดระบุ ผต. ตามด้วยเลข (เช่น ผต.1234)"
        />
        <div style={{ marginBottom: 8 }}>
          <label style={{ display: 'block', fontSize: 13 }}>
            วันหมดอายุหนังสือรับรองผู้ตรวจประเมิน
          </label>
          <input
            className="form-control"
            type="date"
            value={inspector.cert_expire || ''}
            onChange={e => this.setInspector('cert_expire', e.target.value)}
          />
        </div>
        <CountedInput
          field="phone"
          label="เบอร์โทรศัพท์ (ผู้ตรวจประเมิน)"
          value={inspector.phone}
          onChange={this.setInspector}
        />
        {this.renderAddress(
          inspector,
          this.setInspector,
          'ที่อยู่ของผู้ตรวจประเมิน',
        )}
        {this.sectionHeader('ข้อมูลเจ้าของอาคาร/โครงการ หรือผู้รับมอบอำนาจ')}
        <CountedInput
          field="full_name"
          label="ชื่อเจ้าของอาคาร/โครงการ หรือผู้รับมอบอำนาจ"
          value={owner.full_name}
          onChange={this.setOwner}
        />
        <div style={{ marginBottom: 8 }}>
          <label style={{ display: 'block', fontSize: 13 }}>อายุ</label>
          <input
            className="form-control"
            type="number"
            value={owner.age || ''}
            onChange={e => this.setOwner('age', e.target.value)}
            style={{ width: 120 }}
          />
        </div>
        <CountedInput
          field="id_card"
          label="เลขประจำตัวประชาชน"
          value={owner.id_card}
          onChange={this.setOwner}
        />
        <CountedInput
          field="phone"
          label="เบอร์โทรศัพท์"
          value={owner.phone}
          onChange={this.setOwner}
        />
        {this.renderAddress(
          owner,
          this.setOwner,
          'ที่อยู่ของเจ้าของอาคาร/โครงการ หรือผู้รับมอบอำนาจ',
        )}
        {/* "ผู้ออกแบบ" + "ราคาค่าก่อสร้างรวม" ย้ายไปดึง AUTO ในขั้น 3
            (เป็นข้อมูลที่ระบุตอนสร้างโครงการแล้ว — FRS 4.2.2 feedback PM 2026-08) */}
        <button className="btn btn-default" onClick={() => this.saveManual(1)}>
          ← บันทึก/ย้อนกลับ
        </button>{' '}
        <button className="btn btn-primary" onClick={() => this.saveManual(3)}>
          บันทึก + ถัดไป →
        </button>
      </div>
    );
  }

  renderStep3() {
    const a = this.state.auto;
    if (this.state.loadingAuto) {
      return <div className="alert alert-info">กำลังโหลดข้อมูลระบบ...</div>;
    }
    if (this.state.autoError) {
      return (
        <div>
          <div className="alert alert-warning">
            ดึงข้อมูลระบบไม่ได้: {this.state.autoError}
            <br />
            <small>
              โครงการนี้อาจยังประเมินไม่ครบ (ไม่มีผล OTTV/SEER/E_total) —
              ฟอร์มต้องใช้โครงการที่ประเมินแล้ว
            </small>
          </div>
          <button className="btn btn-default" onClick={() => this.goToStep(2)}>
            ← กลับไปขั้น 2
          </button>{' '}
          <button className="btn btn-primary" onClick={this.loadAuto}>
            ลองใหม่
          </button>
        </div>
      );
    }
    if (!a) {
      return (
        <div>
          <p>ยังไม่ได้โหลดข้อมูลระบบ</p>
          <button className="btn btn-primary" onClick={this.loadAuto}>
            โหลดข้อมูลระบบ (AUTO)
          </button>
        </div>
      );
    }
    const ad = a.area_detail || {};
    const areaRow = (label, val) => (
      <tr>
        <td style={{ paddingLeft: 28 }}>{label}</td>
        <td>{val || val === 0 ? val : '-'} ตร.ม.</td>
      </tr>
    );
    return (
      <div>
        {this.sectionHeader('ข้อมูลทั่วไปของอาคาร')}
        <table className="table table-bordered">
          <tbody>
            <tr>
              <td>ชื่อโครงการ/อาคาร</td>
              <td>{a.project_name}</td>
            </tr>
            <tr>
              <td>ประเภทอาคาร</td>
              <td>{a.building_type}</td>
            </tr>
            <tr>
              <td>สถานที่ตั้ง</td>
              <td>{a.building_location}</td>
            </tr>
            <tr>
              <td>ผู้ออกแบบ</td>
              <td>{a.designer || '-'}</td>
            </tr>
            <tr>
              <td>
                <strong>พื้นที่อาคารทั้งหมด</strong>
              </td>
              <td>
                <strong>{ad.total || '-'} ตร.ม.</strong>
              </td>
            </tr>
            {areaRow('พื้นที่ใช้สอยรวม', ad.usable)}
            {areaRow('พื้นที่ใช้สอยที่ปรับอากาศ', ad.ac)}
            {areaRow('พื้นที่ใช้สอยที่ไม่ปรับอากาศ', ad.no_ac)}
            {areaRow('พื้นที่จอดรถในตัวอาคาร', ad.parking)}
            {areaRow('พื้นที่ใช้สอยบนดาดฟ้า', ad.roof_top)}
            {areaRow('พื้นที่ใช้สอยอื่น ๆ', ad.other)}
            <tr>
              <td>ผ่านทุกรายระบบ</td>
              <td>{a.pass_all_systems ? '✓' : '-'}</td>
            </tr>
            <tr>
              <td>ผ่านรวมของอาคาร</td>
              <td>{a.pass_overall ? '✓' : '-'}</td>
            </tr>
          </tbody>
        </table>
        {this.sectionHeader('ข้อมูลรูปแบบอาคาร และอุปกรณ์การใช้พลังงาน')}
        {this.renderManualField('จำนวนชั้น/ความสูง', 'floor_height')}
        {this.renderManualField(
          'ชนิดและความหนาของผนัง',
          'wall',
          1,
          'เช่น คอนกรีตมวลเบาฉาบปูน ความหนา 10 เซนติเมตร',
        )}
        {this.renderManualField(
          'ชนิดและความหนาของกระจก',
          'glass',
          1,
          'เช่น กระจกใส ความหนา 6 มิลลิเมตร',
        )}
        <div style={{ marginBottom: 8 }}>
          <label style={{ display: 'block', fontSize: 13 }}>
            WWR A/C zone เฉลี่ย (%) — ดึงจากโปรแกรม (แก้ไม่ได้)
          </label>
          <input
            className="form-control"
            value={(a.structure && a.structure.wwr) || ''}
            readOnly
            style={{ background: '#eee' }}
          />
        </div>
        {this.renderManualField(
          'ชนิดและความหนาของหลังคา',
          'roof',
          1,
          'เช่น หลังคาคอนกรีตเสริมเหล็ก ความหนา 12 เซนติเมตร พร้อมติดฉนวนกันความร้อน',
        )}
        {this.renderManualField('ชนิดเครื่องปรับอากาศ', 'ac', 3)}
        {this.renderManualField(
          'ชนิดอุปกรณ์ไฟฟ้าแสงสว่าง',
          'lighting',
          1,
          'เช่น หลอดไฟ LED ขนาด 9 – 36 วัตต์',
        )}
        {this.renderManualField(
          'ชนิดอุปกรณ์ผลิตน้ำร้อน',
          'hot_water',
          1,
          'เช่น หม้อต้มน้ำที่ใช้น้ำมันเป็นเชื้อเพลิง',
        )}
        <h5 style={{ marginTop: 16 }}>
          ผลการประเมินแบบอาคารด้วยโปรแกรมประเมินค่าอนุรักษ์พลังงาน
        </h5>
        <table className="table table-bordered">
          <thead>
            <tr>
              <th>เกณฑ์มาตรฐาน</th>
              <th>ค่ามาตรฐาน</th>
              <th>อาคารที่ออกแบบ</th>
              <th>ผลประเมิน</th>
            </tr>
          </thead>
          <tbody>
            {(a.eval_rows || []).map((r, i) => (
              <tr key={i}>
                <td>{r.label}</td>
                <td>{r.ref}</td>
                <td>{r.value}</td>
                <td>{r.result}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <button
          className="btn btn-default"
          onClick={() => this.setState({ step: 2 })}
        >
          ← ย้อนกลับ
        </button>{' '}
        <button
          className="btn btn-primary"
          onClick={this.saveStructureAndPreview}
        >
          บันทึก + ไป Preview →
        </button>
      </div>
    );
  }

  // input manual ของรูปแบบอาคาร/อุปกรณ์ (textarea เผื่อข้อความยาว เช่น ชนิดแอร์)
  renderManualField(label, field, rows = 1, placeholder = '') {
    return (
      <div style={{ marginBottom: 8 }}>
        <label style={{ display: 'block', fontSize: 13 }}>{label}</label>
        <textarea
          className="form-control"
          rows={rows}
          placeholder={placeholder}
          style={{ resize: 'vertical' }}
          value={this.state.manual[field] || ''}
          onChange={e => this.setManual(field, e.target.value)}
        />
      </div>
    );
  }

  renderStep4(locked) {
    // สถานะเดียว รวมทั้ง preview/submit/revoke (ตาม feedback PM: ให้อยู่ที่เดียว ไม่เด้งขึ้นลง)
    const statusText =
      this.state.loading && !this.state.previewUrl
        ? 'กำลังสร้าง Preview...'
        : this.state.loading
        ? 'กำลังดำเนินการ...'
        : null;
    return (
      <div>
        {this.sectionHeader('ตรวจสอบและยืนยันข้อมูล')}
        <p style={{ color: '#666', fontSize: 13 }}>
          Preview เฉพาะ 3 ไฟล์ที่ระบบสร้าง: ออพ.๐๑ + ออพ.๐๒ + แบบสรุปผล BEC
        </p>
        {!this.state.loading && !this.state.previewUrl && (
          <button className="btn btn-primary" onClick={this.loadPreview}>
            โหลด Preview
          </button>
        )}
        {this.state.previewUrl && (
          <iframe
            title="preview"
            src={this.state.previewUrl}
            style={{ width: '100%', height: 600, border: '1px solid #ccc' }}
          />
        )}
        <div style={{ marginTop: 12 }}>
          {/* สถานะเดียว วางไว้ตรงนี้ (ใกล้กล่องผลลัพธ์/ปุ่มดำเนินการ) */}
          {statusText && <div className="alert alert-info">{statusText}</div>}
          {!locked && (
            <button className="btn btn-success" onClick={this.handleSubmit}>
              สร้างเอกสาร และส่งตรวจ (Submit)
            </button>
          )}
          {locked && (
            <div>
              <div className="alert alert-success">
                {/* ข้อความ ไทย/อังกฤษ ตามภาษาที่เลือก (feedback PM) */}
                {this.isThai() ? (
                  <span>
                    สร้างเอกสารสำเร็จ (เลขที่เอกสาร{' '}
                    <strong>{this.state.form.doc_number}</strong>, ครั้งที่แก้ไข{' '}
                    {this.state.form.revision_no})
                  </span>
                ) : (
                  <span>
                    Document Generated Successfully (Document No.{' '}
                    <strong>{this.state.form.doc_number}</strong>, Revision{' '}
                    {this.state.form.revision_no})
                  </span>
                )}
                {this.state.form.bundle_file_url && (
                  <span>
                    {' '}
                    —{' '}
                    <a
                      href={this.state.form.bundle_file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {this.isThai() ? 'ดาวน์โหลด' : 'Download'}
                    </a>
                  </span>
                )}
              </div>
              <button
                className="btn btn-warning"
                onClick={() => this.setState({ showRevoke: true })}
              >
                ถอนคำขอ
              </button>
            </div>
          )}
        </div>

        {this.state.showRevoke && (
          <div
            className="panel panel-warning"
            style={{ padding: 12, marginTop: 12 }}
          >
            <p>
              ข้อควรระวัง: เอกสารที่พิมพ์ออกไปแล้วอาจอยู่ระหว่างการใช้ประกอบการ
              ยื่นขออนุญาต โปรดยืนยันเพื่อดำเนินการถอนคำขอ
            </p>
            <textarea
              className="form-control"
              placeholder="โปรดระบุเหตุผลในการถอนคำขอ (จำเป็น)"
              value={this.state.revokeReason}
              onChange={e => this.setState({ revokeReason: e.target.value })}
            />
            <button
              className="btn btn-danger"
              style={{ marginTop: 8 }}
              onClick={this.handleRevoke}
            >
              ยืนยันการถอนคำขอ
            </button>{' '}
            <button
              className="btn btn-default"
              style={{ marginTop: 8 }}
              onClick={() => this.setState({ showRevoke: false })}
            >
              ยกเลิก
            </button>
          </div>
        )}

        <h4 style={{ marginTop: 24 }}>ประวัติการส่งตรวจ และถอนการส่งตรวจ</h4>
        {/* ประวัติแสดงอัตโนมัติ ไม่ต้องกดโหลด (feedback PM) — โหลดตอนเข้า/หลัง submit */}
        <table className="table table-bordered table-condensed">
          <thead>
            <tr>
              <th>การกระทำ</th>
              <th>เวลา</th>
              <th>ครั้งที่แก้ไข</th>
              <th>เหตุผล</th>
            </tr>
          </thead>
          <tbody>
            {this.state.auditLog.length === 0 && (
              <tr>
                <td colSpan={4} style={{ color: '#999' }}>
                  ยังไม่มีประวัติ
                </td>
              </tr>
            )}
            {this.state.auditLog.map(l => (
              <tr key={l.id}>
                <td>{this.actionText(l.action)}</td>
                <td>{this.fmtDateTime(l.action_at)}</td>
                <td>{l.revision_no}</td>
                <td>{l.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
}

export default FormWizardPage;



// WEBPACK FOOTER //
// ./src/pages/forms/FormWizardPage.js